import { Button } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { utils, writeFile } from 'xlsx';

import {
  date as commonDate,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';

import {
  TaskFilterGQLType,
  useGetTasksQuery,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  getActionRequiredFromCategorySpecificProperties,
  getTrucatedLoanIdsFromCategorySpecificProperties,
  UNASSIGNED_USER,
} from './common';
import { formatCurrencyAmount } from 'packages/common-frontend/utils';

export const DownloadTaskCenterButton = ({
  groupByType,
  filterState,
}: {
  groupByType: 'status' | 'user';
  filterState: TaskFilterGQLType;
}) => {
  const { __typename: _unused, ...filters } = filterState;

  const getTasksQuery = useGetTasksQuery({ variables: { filters } });

  const tasksByGroup = useMemo(() => {
    if (getTasksQuery.data) {
      switch (groupByType) {
        case 'status': {
          return _.groupBy(
            getTasksQuery.data.getTasks.tasks,
            (t) => t.taskEntity.status,
          );
        }
        case 'user': {
          return _.groupBy(getTasksQuery.data.getTasks.tasks, (t) =>
            t.taskEntity.assignedUser
              ? t.taskEntity.assignedUser.id
              : UNASSIGNED_USER,
          );
        }
        default: {
          return finleyAssertUnreachable(groupByType);
        }
      }
    }
    return null;
  }, [getTasksQuery.data, groupByType]);

  // download add data into excel book
  const exportToXlsx = useCallback(() => {
    if (tasksByGroup) {
      const keys = Object.keys(tasksByGroup);

      const todayFullDateStr = commonDate.currentDateStr();

      const wb = utils.book_new();

      keys.forEach((key) => {
        const tasks = tasksByGroup[key];

        if (tasks && tasks.length > 0) {
          const dataForTask = tasks.map((t) => {
            const loanId = getTrucatedLoanIdsFromCategorySpecificProperties(
              t.domainProperties.categorySpecificProperties,
            );

            return {
              // this should be the same as the columnDefs for the tanstack tables in the child component
              status: _.startCase(t.taskEntity.status),
              creditFacilityName: _.startCase(
                t.domainProperties.creditFacility.name,
              ),
              loanId,
              category: _.startCase(
                t.domainProperties.categorySpecificProperties.category,
              ),
              actionRequired: getActionRequiredFromCategorySpecificProperties(
                t.domainProperties.categorySpecificProperties,
              ),
              amount: formatCurrencyAmount(
                t.domainProperties.amount,
                Bank.Currency.USD,
              ),
              partyName: _.startCase(t.domainProperties.party.name),
              dueDate: commonDate.fullDateStrToShortMonthDayYearString(
                t.taskEntity.dueDate,
              ),
              assignedUser: t.taskEntity.assignedUser
                ? `${t.taskEntity.assignedUser.firstName} ${t.taskEntity.assignedUser.lastName}`
                : '-',
            };
          });

          const ws = utils.json_to_sheet(dataForTask);

          switch (groupByType) {
            case 'status': {
              utils.book_append_sheet(wb, ws, key);
              break;
            }
            case 'user': {
              // we want the sheet names to be the names of the users, not user uuids.
              // since we are grouping these tasks by user id, we can just fetch the full name
              // from the first task in the group.
              const firstTask = tasks[0];

              const name = firstTask.taskEntity.assignedUser
                ? `${firstTask.taskEntity.assignedUser.firstName} ${firstTask.taskEntity.assignedUser.lastName}`
                : 'Unassigned';

              utils.book_append_sheet(wb, ws, name);
              break;
            }
            default: {
              finleyAssertUnreachable(groupByType);
            }
          }
        }
      });

      writeFile(wb, `task-center-${todayFullDateStr}.xlsx`);
    }
  }, [groupByType, tasksByGroup]);

  if (getTasksQuery.error) throw getTasksQuery.error;

  if (!tasksByGroup) {
    return null;
  }

  return (
    <Button variant="secondary" colorScheme="v2-aurora" onClick={exportToXlsx}>
      Download XLSX
    </Button>
  );
};
